import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { seoExecutiveReport } from "@/lib/seo/seo-os.functions";
import { SeoPage, Stat, Panel, Loading, Empty, DataTable, num, pct } from "@/components/admin/seo/SeoUi";

export const Route = createFileRoute("/admin/seo/reports")({
  head: () => ({ meta: [{ title: "Weekly report · Admin" }, { name: "robots", content: "noindex, nofollow" }] }),
  component: ReportsPage,
});

function ReportsPage() {
  const load = useServerFn(seoExecutiveReport);
  const q = useQuery({ queryKey: ["seo-report"], queryFn: () => load() });
  const d = q.data as any;

  return (
    <SeoPage
      title="Weekly summary"
      description="One page covering how search traffic moved, what opportunities exist, what the system changed or wants to change, and anything blocking progress."
    >
      {q.isLoading ? (
        <Loading />
      ) : (
        <div className="grid gap-4">
          <p className="text-xs text-muted-foreground">
            {d.period.start} → {d.period.end} compared with {d.period.previousStart} → {d.period.previousEnd}
          </p>

          {d.search.hasData ? (
            <div className="grid gap-3 sm:grid-cols-4">
              <Stat label="Clicks" value={num(d.search.current.clicks)} sub={`${d.search.delta.clicks >= 0 ? "+" : ""}${num(d.search.delta.clicks)} vs prior week`} />
              <Stat label="Impressions" value={num(d.search.current.impressions)} sub={`${d.search.delta.impressions >= 0 ? "+" : ""}${num(d.search.delta.impressions)}`} />
              <Stat label="Click-through rate" value={pct(d.search.current.ctr)} />
              <Stat label="Average position" value={d.search.current.position.toFixed(1)} sub={`${d.search.delta.position <= 0 ? "improved" : "slipped"} ${Math.abs(d.search.delta.position)}`} />
            </div>
          ) : (
            <Empty>No search data for this period yet.</Empty>
          )}

          <div className="grid gap-4 lg:grid-cols-2">
            <Panel title="Opportunities">
              <ul className="space-y-1 text-sm">
                <li>Close to the top three: {num(d.opportunities.top3)}</li>
                <li>Just off page one: {num(d.opportunities.page1)}</li>
                <li>Longer-term lift: {num(d.opportunities.lift)}</li>
                <li>Under-clicking pages: {num(d.opportunities.ctr)}</li>
                <li>Estimated clicks left on the table: {num(d.opportunities.estimatedMissedClicks)} / month</li>
              </ul>
            </Panel>
            <Panel title="What the system did">
              <ul className="space-y-1 text-sm">
                <li>Suggestions created: {num(d.actions.created)}</li>
                <li>Applied: {num(d.actions.autoApplied)}</li>
                <li>Awaiting your approval: {num(d.actions.awaitingApproval)}</li>
                <li>Blocked by rules or compliance: {num(d.actions.blocked)}</li>
                <li>Undone: {num(d.actions.rolledBack)}</li>
              </ul>
            </Panel>
          </div>

          <Panel title="Enquiries this week" subtitle={d.conversions.note}>
            <p className="text-2xl font-black">{num(d.conversions.leadsThisWeek)}</p>
          </Panel>

          <Panel title="Competitor movement">
            {d.competitors?.length ? (
              <DataTable
                head={["What changed", "Impact", "What it means"]}
                rows={d.competitors.map((c: any) => [c.kind.replace(/_/g, " "), c.severity, c.suggestion])}
              />
            ) : (
              <Empty>No competitor changes recorded this week.</Empty>
            )}
          </Panel>

          {d.blockers?.length ? (
            <Panel title="Blockers">
              <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
                {d.blockers.map((b: string) => (
                  <li key={b}>{b}</li>
                ))}
              </ul>
            </Panel>
          ) : null}
        </div>
      )}
    </SeoPage>
  );
}
